import React, { useState, useCallback, useMemo } from 'react';
import { Alert } from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { useLabourGroupsPanelStore } from '../stores/labourGroupsPanelStore';
import { useUnifiedGroupMutation } from '../hooks/useUnifiedGroupMutation';
import { useAvailableLabourItems } from '../hooks/useAvailableLabourItems';
import { GroupNameCard } from './panel/molecules/GroupNameCard';
import { LabourItemsAssignmentCard } from './panel/molecules/LabourItemsAssignmentCard';
import { GroupFormActions } from './panel/molecules/GroupFormActions';

const GroupCreateForm: React.FC = () => {
  const editedGroupName = useLabourGroupsPanelStore(
    state => state.editedGroupName
  );
  const setEditedGroupName = useLabourGroupsPanelStore(
    state => state.setEditedGroupName
  );
  const transferListSearch = useLabourGroupsPanelStore(
    state => state.transferListSearch
  );
  const setTransferListSearch = useLabourGroupsPanelStore(
    state => state.setTransferListSearch
  );
  const closePanel = useLabourGroupsPanelStore(state => state.closePanel);
  const resetState = useLabourGroupsPanelStore(state => state.resetState);

  const [assignedItems, setAssignedItems] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const { data: allAvailableItems = [], isLoading: itemsLoading } =
    useAvailableLabourItems();

  const availableItems = useMemo(() => {
    const assignedIds = new Set(assignedItems.map(item => item.id));
    return allAvailableItems.filter(item => !assignedIds.has(item.id));
  }, [allAvailableItems, assignedItems]);

  const {
    mutate: saveGroup,
    isPending,
    error: mutationError,
  } = useUnifiedGroupMutation();

  const handleSave = useCallback(async () => {
    if (!editedGroupName.trim()) {
      return;
    }

    // Create group with items in single API call
    saveGroup(
      {
        name: editedGroupName.trim(),
        labourItemDefinitionIds: assignedItems.map(item => item.id),
      },
      {
        onSuccess: () => {
          closePanel();
          resetState();
        },
      }
    );
  }, [editedGroupName, assignedItems, saveGroup, closePanel, resetState]);

  const handleCancel = () => {
    closePanel();
    resetState();
  };

  const handleMoveToAssigned = useCallback(
    (itemId: string) => {
      const item = availableItems.find(item => item.id === itemId);
      if (item) {
        setAssignedItems(prev => [...prev, item]);
      }
    },
    [availableItems]
  );

  const handleMoveToAvailable = useCallback((itemId: string) => {
    setAssignedItems(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const isFormValid = editedGroupName.trim().length > 0;

  return (
    <div className="flex flex-col gap-6">
      {mutationError && (
        <Alert severity="error">{getErrorMessage(mutationError)}</Alert>
      )}

      {/* Group Name Card */}
      <GroupNameCard
        name={editedGroupName}
        onChange={setEditedGroupName}
        disabled={isPending}
        autoFocus
        dataTestId="create-group-name-input"
      />

      {/* Labour Items Assignment Card */}
      <LabourItemsAssignmentCard
        availableItems={availableItems}
        assignedItems={assignedItems}
        searchText={transferListSearch}
        onSearchChange={setTransferListSearch}
        onMoveToAssigned={handleMoveToAssigned}
        onMoveToAvailable={handleMoveToAvailable}
        loading={itemsLoading || isPending}
        optional
      />

      {/* Actions */}
      <GroupFormActions
        onCancel={handleCancel}
        onSubmit={handleSave}
        submitLabel="Create Group"
        disabled={!isFormValid}
        loading={isPending}
        cancelTestId="cancel-create-button"
        submitTestId="create-group-button"
      />
    </div>
  );
};

export default GroupCreateForm;
